import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";
import { stripSystemTags } from "@/lib/messageMask";

type Message = Database["public"]["Tables"]["messages"]["Row"];
type Booking = Database["public"]["Tables"]["bookings"]["Row"];
type RefusalReason = Database["public"]["Enums"]["refusal_reason"];

const REFUSAL_LABELS: Record<RefusalReason, string> = {
  distance_trop_importante: "Distance trop importante",
  pas_disponible: "Créneau indisponible",
  autre: "Autre raison",
};

const formatTime = (t: string) => t.slice(0, 5);

const formatDate = (d: string) => {
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};

export const useNotifications = () => {
  const { user } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (!user) return;

    // New messages
    const messagesChannel = supabase
      .channel("notif-messages")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        async (payload) => {
          const msg = payload.new as Message;
          if (msg.sender_id === userRef.current?.id) return;

          const { data: convo } = await supabase
            .from("conversations")
            .select("parent_id, pro_id")
            .eq("id", msg.conversation_id)
            .single();

          if (!convo) return;
          if (convo.parent_id !== userRef.current?.id && convo.pro_id !== userRef.current?.id) return;

          const { data: sender } = await supabase
            .from("profiles")
            .select("full_name")
            .eq("user_id", msg.sender_id)
            .single();

          const preview = stripSystemTags(msg.content);
          if (!preview) return;
          toast.message("Nouveau message", {
            description: `${sender?.full_name ?? "Quelqu'un"} : ${preview.slice(0, 100)}${preview.length > 100 ? "…" : ""}`,
            icon: "💬",
            duration: 6000,
          });
        }
      )
      .subscribe();

    // Booking changes
    const bookingsChannel = supabase
      .channel("notif-bookings")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "bookings" },
        async (payload) => {
          const booking = payload.new as Booking;
          const old = payload.old as Partial<Booking>;

          if (old.status === booking.status) return;

          const dateStr = formatDate(booking.booking_date);
          const timeStr = `${formatTime(booking.start_time)} – ${formatTime(booking.end_time)}`;

          const isParticipant =
            booking.parent_id === userRef.current?.id ||
            booking.pro_id === userRef.current?.id;

          // Lifecycle notifications (parent + pro)
          if (isParticipant) {
            if (booking.status === "in_progress") {
              toast.info("Prestation démarrée", {
                description: `${dateStr} • ${timeStr} — La prestation est en cours.`,
                duration: 7000,
                icon: "▶️",
              });
            } else if (booking.status === "completed") {
              toast.message("Prestation terminée", {
                description: `${dateStr} • ${timeStr} — En attente de validation des deux parties (auto-validation 48h).`,
                duration: 8000,
                icon: "🏁",
              });
            } else if (booking.status === "validated") {
              toast.success("Prestation validée", {
                description: `${dateStr} • ${timeStr} — Vous pouvez désormais laisser un avis.`,
                duration: 9000,
                icon: "🛡️",
              });
            }
          }

          // Notify parent
          if (booking.parent_id === userRef.current?.id) {
            if (booking.status === "accepted") {
              toast.success("Réservation confirmée !", {
                description: `${dateStr} de ${timeStr} — Votre garde est confirmée. Vous pouvez contacter le professionnel via la messagerie.`,
                duration: 8000,
                icon: "✅",
              });
            } else if (booking.status === "refused") {
              const reasonLabel = booking.refusal_reason
                ? REFUSAL_LABELS[booking.refusal_reason]
                : "Non précisée";

              const details = booking.refusal_details || reasonLabel;

              toast.error("Réservation refusée", {
                description: `${dateStr} de ${timeStr}\n\n📋 Motif : ${reasonLabel}${booking.refusal_details && booking.refusal_reason !== "autre" ? "" : ""}\n💬 Détail : ${details}\n\nVous pouvez rechercher un autre professionnel disponible.`,
                duration: 12000,
                icon: "❌",
              });
            }
          }

          // Notify pro
          if (booking.pro_id === userRef.current?.id && booking.status === "pending") {
            toast.info("Nouvelle demande de réservation", {
              description: `${dateStr} de ${timeStr} — Un parent souhaite réserver ce créneau. Rendez-vous sur votre planning pour répondre.`,
              duration: 8000,
              icon: "📅",
            });
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "bookings" },
        async (payload) => {
          const booking = payload.new as Booking;
          if (booking.pro_id !== userRef.current?.id) return;

          const dateStr = formatDate(booking.booking_date);
          const timeStr = `${formatTime(booking.start_time)} – ${formatTime(booking.end_time)}`;

          toast.info("Nouvelle demande de réservation", {
            description: `${dateStr} de ${timeStr} — ${booking.children_count ?? 1} enfant${(booking.children_count ?? 1) > 1 ? "s" : ""}${booking.special_needs ? ` • Note : ${booking.special_needs}` : ""}`,
            duration: 8000,
            icon: "📅",
          });
        }
      )
      .subscribe();

    // Care request moderation notifications
    const careRequestsChannel = supabase
      .channel("notif-care-requests")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "care_requests" },
        async (payload) => {
          const request = payload.new as any;
          const old = payload.old as any;

          if (old.status === request.status) return;
          if (request.parent_id !== userRef.current?.id) return;

          if (request.status === "accepted") {
            toast.success("Annonce acceptée !", {
              description: `Votre annonce "${request.title}" a été validée par l'administration.${request.admin_message ? `\n💬 ${request.admin_message}` : ""}`,
              duration: 10000,
              icon: "✅",
            });
          } else if (request.status === "refused") {
            toast.error("Annonce refusée", {
              description: `Votre annonce "${request.title}" a été refusée.${request.admin_message ? `\n💬 ${request.admin_message}` : ""}`,
              duration: 10000,
              icon: "❌",
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(bookingsChannel);
      supabase.removeChannel(careRequestsChannel);
    };
  }, [user]);
};
